import { photosFor, type PaymentMethod, type PaymentsConfig } from '@trendator/shared';
import { useEffect, useState, type ComponentType } from 'react';
import { ApiError, api } from '../api';
import { BackIcon, CardIcon, CoinIcon } from '../components/icons';
import { useBackButton } from '../hooks';
import { haptic, openPayUrl } from '../telegram';
import { rub } from '../format';
import { Loading } from './Status';

const METHODS: Record<PaymentMethod, { label: string; color: string; Icon: ComponentType }> = {
  card_sbp: { label: 'Карта или СБП', color: '#34C759', Icon: CardIcon },
  usdt: { label: 'Крипта · USDT', color: '#30B0C7', Icon: CoinIcon },
};

export interface TopUpPreset {
  packageId?: string;
  method?: PaymentMethod;
}

/** Пополнение — docs/design/TopUp.dc.html. */
export function TopUp({
  balance,
  preset,
  onBack,
  onPaymentCreated,
}: {
  balance: number;
  preset?: TopUpPreset;
  onBack: () => void;
  onPaymentCreated: (paymentId: string) => void;
}) {
  useBackButton(onBack);
  const [config, setConfig] = useState<PaymentsConfig | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [packageId, setPackageId] = useState<string | null>(preset?.packageId ?? null);
  const [method, setMethod] = useState<PaymentMethod | null>(preset?.method ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .paymentsConfig()
      .then((cfg) => {
        setConfig(cfg);
        setPackageId((id) => (id && cfg.packages.some((p) => p.id === id) ? id : cfg.defaultPackageId));
        setMethod((m) => (m && cfg.methods.includes(m) ? m : (cfg.methods[0] ?? null)));
      })
      .catch(() => setLoadError(true));
  }, []);

  if (loadError) {
    return (
      <main className="screen">
        <TopBar balance={balance} onBack={onBack} />
        <div className="center">
          <h1>Нет связи</h1>
          <p>Не получилось загрузить пакеты. Проверь интернет.</p>
        </div>
      </main>
    );
  }
  if (!config) return <Loading />;

  const pkg = config.packages.find((p) => p.id === packageId) ?? config.packages[0]!;
  const noMethods = config.methods.length === 0;

  const pay = async () => {
    if (!method || busy) return;
    haptic();
    setBusy(true);
    setError(null);
    try {
      const payment = await api.createPayment(pkg.id, method);
      // Telegram может не дать открыть ссылку не по клику — тогда юзер нажмёт «Перейти к оплате» на следующем экране.
      if (payment.payUrl) {
        try {
          openPayUrl(payment.payUrl);
        } catch {
          /* откроет кнопкой */
        }
      }
      onPaymentCreated(payment.id);
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'too_many_payments'
          ? 'Много неоплаченных счетов. Оплати открытый или подожди 10 минут.'
          : 'Платёжка не ответила. Попробуй ещё раз.',
      );
      setBusy(false);
    }
  };

  return (
    <main className="screen topup">
      <TopBar balance={balance} onBack={onBack} />

      <div className="topup__intro">
        <h1 className="page-title">Пополнить</h1>
        <p className="page-lead">1 кредит = 1 ₽. Цена видна до генерации, кредиты не сгорают.</p>
      </div>

      <fieldset className="packs">
        <legend className="sr-only">Сумма пополнения</legend>
        {config.packages.map((p) => {
          const selected = p.id === pkg.id;
          return (
            <label key={p.id} className={`pack${selected ? ' pack--selected' : ''}`}>
              <input
                type="radio"
                name="pack"
                checked={selected}
                onChange={() => {
                  haptic('select');
                  setPackageId(p.id);
                }}
              />
              <span className="pack__amount">{rub(p.amount)}</span>
              {p.bonus > 0 && (
                <span className={`pack__bonus${selected ? ' pack__bonus--on' : ''}`}>
                  +{p.bonusPercent}% · {p.credits.toLocaleString('ru-RU')}
                </span>
              )}
              <span className="pack__hint">≈ {photosFor(p.credits)} фото</span>
            </label>
          );
        })}
      </fieldset>

      <fieldset className="methods">
        <legend className="methods__legend">Способ оплаты</legend>
        {noMethods ? (
          <p className="methods__empty">Оплата подключается — загляни чуть позже.</p>
        ) : (
          <div className="glass-list">
            {config.methods.map((m, i) => {
              const { label, color, Icon } = METHODS[m];
              return (
                <div key={m}>
                  {i > 0 && <div className="glass-list__sep" />}
                  <label className="glass-list__row">
                    <span className="glass-list__icon" style={{ background: color }}>
                      <Icon />
                    </span>
                    <span className="glass-list__label">{label}</span>
                    <input
                      type="radio"
                      name="pay"
                      checked={method === m}
                      onChange={() => {
                        haptic('select');
                        setMethod(m);
                      }}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </fieldset>

      {error && (
        <p className="error-text" role="alert" style={{ marginTop: 14 }}>
          {error}
        </p>
      )}

      <div className="paybar">
        <div className="paybar__info">
          <span className="paybar__credits">{pkg.credits.toLocaleString('ru-RU')} кредитов</span>
          <span className="paybar__sub">придут сразу</span>
        </div>
        <button type="button" className="btn-accent paybar__btn" disabled={!method || busy} onClick={pay}>
          {busy ? 'Открываем…' : `Оплатить ${rub(pkg.amount)}`}
        </button>
      </div>
    </main>
  );
}

function TopBar({ balance, onBack }: { balance: number; onBack: () => void }) {
  return (
    <header className="topbar">
      <button type="button" className="glass icon-btn" aria-label="Назад" onClick={onBack}>
        <BackIcon />
      </button>
      <span className="glass balance-chip">
        Баланс&nbsp;<b>{rub(balance)}</b>
      </span>
    </header>
  );
}
